<template>
  <div id="app">
    <b-navbar toggleable="lg" type="dark" variant="primary" sticky style="z-index: 10000;" v-if="user != null">
      <b-navbar-brand :to="{ name: 'home'}">
        <font-awesome-icon icon="running" size="1x" id="icon"/>
      </b-navbar-brand>
      <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
      <b-collapse id="nav-collapse" is-nav>
        <b-navbar-nav>
          <!-- Group -->
          <b-nav-item-dropdown v-if="currentOrganization">
            <template #button-content>
              <font-awesome-icon icon="sitemap" size="1x"/>
              {{ currentOrganization.name }}
            </template>
            <b-dropdown-item
                v-for="organization in organizations"
                :key="organization.group.uuid" @click="setCurrentOrganization(organization.group.uuid)"
            >
              {{ organization.group.name }}
            </b-dropdown-item>
          </b-nav-item-dropdown>
          <!-- Team -->
          <b-nav-item-dropdown v-if="currentOrganization">
            <template #button-content>
              <font-awesome-icon icon="users" size="1x"/>
              {{ currentTeam ? currentTeam.name : '' }}
            </template>
            <b-dropdown-item
                v-for="team in teams"
                :key="team.group.uuid" @click="setCurrentTeam(team.group.uuid)"
            >
              {{ team.group.name }}
            </b-dropdown-item>
          </b-nav-item-dropdown>

          <!--            <b-nav-item :to="{ name: 'groups'}">Groups</b-nav-item>-->
          <b-nav-item :to="{ name: 'runners'}" v-if="canViewRunners">Runners</b-nav-item>
          <b-nav-item :to="{ name: 'workouts'}">Workouts</b-nav-item>
          <b-nav-item :to="{ name: 'plans'}" v-if="isSuperAdmin">Plans</b-nav-item>
          <b-nav-item :to="{ name: 'programs'}" v-if="canManagePrograms">Programs</b-nav-item>
          <b-nav-item :to="{ name: 'my-activities'}">My Activities</b-nav-item>
          <!--            <b-nav-item :to="{ name: 'plan'}">Plan</b-nav-item>-->
        </b-navbar-nav>

        <!-- Right aligned nav items -->
        <b-navbar-nav class="ml-auto">


          <b-nav-item-dropdown right v-if="isOrganizationAdmin || isSuperAdmin">
            <template #button-content>
              Manage
            </template>
            <b-dropdown-item :to="{ name: 'users-and-teams'}" v-if="isOrganizationAdmin">Users and Teams</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'my-organizations'}" v-if="isOrganizationAdmin">My Organizations</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'labels'}" v-if="isOrganizationAdmin">Labels</b-dropdown-item>
            <b-dropdown-divider v-if="isOrganizationAdmin && isSuperAdmin"/>
            <b-dropdown-item :to="{ name: 'organizations'}" v-if="isSuperAdmin">Organizations</b-dropdown-item>
            <b-dropdown-item :to="{ name: 'users-and-roles'}" v-if="isSuperAdmin">All Users</b-dropdown-item>
            <b-dropdown-item v-if="isSuperAdmin" link-class="menu-item-with-shortcut" @click="openImpersonateSearch">
              <font-awesome-icon icon="mask" size="1x" class="mr-1"/>
              Impersonate
              <span class="menu-shortcut">{{ shortcutLabelOf('impersonate') }}</span>
            </b-dropdown-item>
            <b-dropdown-divider v-if="isSuperAdmin"/>
            <b-dropdown-item :to="{ name: 'jobs'}" v-if="isSuperAdmin">
              <font-awesome-icon icon="cogs" size="1x"/>
              Jobs
            </b-dropdown-item>
            <b-dropdown-divider v-if="isSuperAdmin"/>
            <b-dropdown-item :to="{ name: 'system'}" v-if="isSuperAdmin">
              <font-awesome-icon icon="cog" size="1x"/>
              System
            </b-dropdown-item>
          </b-nav-item-dropdown>

          <b-nav-item
              v-if="currentOrganization"
              class="mr-2"
              :title="`Search (${shortcutLabelOf('search')})`"
              @click="openSearch"
          >
            <font-awesome-icon icon="search" size="1x"/>
          </b-nav-item>

          <!-- Owners, Admins and Coaches only: runners have no problems to see (OSA-105) -->
          <NotificationBell v-if="canViewRunners" :jobs="jobs" :problems="problems" :can-manage-teams="isOrganizationAdmin"
                            @dismiss="dismissJob" @dismiss-problem="dismissProblem" @read-problems="markProblemsRead"/>

          <b-avatar v-bind:src="avatar" class="d-none d-lg-block mt-1" size="2em"></b-avatar>
          <b-nav-item-dropdown right>
            <!-- Using 'button-content' slot -->

            <template #button-content>
              <em>{{ displayName }}</em>
            </template>
            <b-dropdown-item :to="{ name: 'profile'}">Profile</b-dropdown-item>
            <b-dropdown-item link-class="menu-item-with-shortcut" @click="showShortcuts = true">
              Keyboard Shortcuts
              <span class="menu-shortcut">{{ shortcutLabelOf('help') }}</span>
            </b-dropdown-item>
            <b-dropdown-item @click="showAbout = true">About</b-dropdown-item>
            <b-dropdown-item @click="logout">Sign Out</b-dropdown-item>
          </b-nav-item-dropdown>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>
    <b-alert variant="danger" class="text-center p-2" :show="impersonationActive">Impersonating as {{ activeUser.name }} ({{ activeUser.username }})
      <b-button @click="endImpersonation()" size="sm" class="ml-1">
        <font-awesome-icon icon="sign-out-alt" size="1x"/>
      </b-button>
    </b-alert>
    <router-view class="router-view" :globalData="globalData" @active-user-changed="loadActiveUser"/>
    <GlobalSearch ref="globalSearch"/>
    <ShortcutsOverlay :show="showShortcuts" :shortcuts="shortcuts" @close="showShortcuts = false"/>
    <AboutBox :show="showAbout" @close="showAbout = false"/>
  </div>
</template>


<script>
import {getAuth} from 'firebase/auth';
import GlobalDataMixin from "@/components/GlobalDataMixin";
import GlobalSearch from "@/components/Search/GlobalSearch";
import {globalSearchProviders, navigationProvider, usersProvider} from "@/components/Search/searchProviders";
import {impersonate} from "@/components/Admin/impersonation";
import ShortcutsOverlay from "@/components/Shortcuts/ShortcutsOverlay";
import AboutBox from "@/components/AboutBox";
import NotificationBell from "@/components/Notifications/NotificationBell";
import {JobSocket} from "@/components/Notifications/jobSocket";
import {findShortcut, shortcutLabel} from "@/components/Shortcuts/shortcuts";

export default {
  name: 'App',
  mixins: [GlobalDataMixin],
  components: {AboutBox, GlobalSearch, NotificationBell, ShortcutsOverlay},
  provide() {
    return {
      // Any page can open the search with its own providers: inject: ['openGlobalSearch']
      openGlobalSearch: options => this.$refs.globalSearch.open(options),
    };
  },
  data() {
    return {
      // The user who is authenticated in the system
      user: Object,
      // The active/impersonated user
      activeUser: {
        type: Object,
        default() {
          return null;
        }
      },
      // The organizations the user belongs to
      organizations: Array,
      teams: Array,
      // Selected organization
      currentOrganization: {
        type: Object,
        default() {
          return null;
        }
      },
      // Selected team
      currentTeam: {
        type: Object,
        default() {
          return null;
        }
      },
      notifications: Number,
      // The user's background jobs, from the jobs WebSocket
      jobs: [],
      // The current organization's open problems, for its Owners, Admins and Coaches (OSA-105)
      problems: [],
      labels: [],
      showShortcuts: false,
      showAbout: false,
    }
  },
  computed: {
    globalData: function () {
      return {
        activeUser: this.activeUser,
        zones: [1, 2, 3, 4, 5],
        labels: this.labels,
        organizations: this.organizations,
        teams: this.teams,
        currentOrganization: this.currentOrganization,
        currentTeam: this.currentTeam
      }
    },
    displayName: function () {
      return this.user ? this.user.displayName : null;
    },
    avatar: function () {
      return this.user ? this.user.photoURL : null;
    },
    impersonationActive() {
      return this.user && this.activeUser.username && this.user.email !== this.activeUser.username;
    },
    // All the keyboard shortcuts. Adding one here also lists it in the "?" overlay.
    shortcuts() {
      return [
        {
          id: 'help',
          key: '?',
          group: 'General',
          description: 'Show keyboard shortcuts',
          handler: () => {
            this.showShortcuts = !this.showShortcuts;
          },
        },
        {
          id: 'search',
          key: 'k',
          mod: true,
          group: 'General',
          description: this.canViewRunners ? 'Search runners and workouts' : 'Search workouts',
          handler: () => {
            this.showShortcuts = false;
            this.openSearch();
          },
        },
        {
          id: 'navigate',
          key: 'g',
          mod: true,
          group: 'General',
          description: 'Go to a page',
          handler: () => {
            this.showShortcuts = false;
            this.openNavigation();
          },
        },
        {
          id: 'impersonate',
          key: 'i',
          mod: true,
          group: 'SuperAdmin',
          description: 'Impersonate a user',
          available: this.isSuperAdmin,
          handler: () => {
            this.showShortcuts = false;
            this.openImpersonateSearch();
          },
        },
      ];
    },
  },
  methods: {
    loadLabels() {
      this.$http.get('/api/labels')
          .then(response => {
            this.labels = response.data;
          })
          .catch((e) => {
            console.error(e)
          });
    },
    loadActiveUser() {
      this.$http.get(`/api/profile`)
          .then(response => {
            console.debug(`Active user: ${response.data.username}`)
            this.activeUser = response.data
            // The server binds the socket to the session's user: connect again after impersonation starts or ends
            this.jobs = [];
            this.jobSocket.connect();
          })
          .catch(e => {
            console.log(e);
          })
      this.getOrganizations();
    },
    getOrganizations() {
      this.$http.get(`/api/groups/current`)
          .then(response => {
            this.parseOrganizationResponse(response.data)

          })
          .catch(e => {
            console.log(e);
          })
    },
    /**
     * The API keeps sessions in memory: after it restarts, this page's next request gets a new session, on the first
     * organization and team, and without the impersonation (OSA-115). Put back what the page shows, where the user still
     * has it; otherwise show what the server has.
     */
    async restoreSession() {
      if (!this.activeUser || !this.activeUser.username) {
        return;
      }
      const wanted = {
        username: this.activeUser.username,
        impersonatedUuid: this.impersonationActive ? this.activeUser.uuid : null,
        organizationUuid: this.currentOrganization ? this.currentOrganization.uuid : null,
        teamUuid: this.currentTeam ? this.currentTeam.uuid : null,
      };
      const has = (groups, uuid) => (groups || []).some(item => item.group.uuid === uuid);
      try {
        let profile = (await this.$http.get('/api/profile')).data;
        if (profile.username !== wanted.username && wanted.impersonatedUuid) {
          console.info('The session was lost: impersonating again');
          await impersonate(this.$http, wanted.impersonatedUuid);
          profile = (await this.$http.get('/api/profile')).data;
          // The socket that just opened belongs to the real user: bind a new one to the impersonated user
          if (profile.username === wanted.username) {
            this.jobSocket.connect();
          }
        }
        if (profile.username !== wanted.username) {
          // Can't be put back: start over as the server's user
          this.loadActiveUser();
          return;
        }

        let groups = (await this.$http.get('/api/groups/current')).data;
        const current = group => (group ? group.uuid : null);
        if (wanted.organizationUuid && current(groups.current_organization) !== wanted.organizationUuid
            && has(groups.organizations, wanted.organizationUuid)) {
          console.info('The session was lost: selecting the organization again');
          groups = (await this.$http.post(`/api/groups/current_organization/${wanted.organizationUuid}`)).data;
        }
        if (wanted.teamUuid && current(groups.current_team) !== wanted.teamUuid && has(groups.teams, wanted.teamUuid)) {
          console.info('The session was lost: selecting the team again');
          groups = (await this.$http.post(`/api/groups/current_team/${wanted.teamUuid}`)).data;
        }
        // What the server has now: the same as before, unless the user lost the organization or team meanwhile
        this.parseOrganizationResponse(groups);
        if (current(groups.current_organization) !== wanted.organizationUuid) {
          this.loadLabels();
        }
        // The bell asked on reconnect too, maybe before the organization was put back
        this.loadProblems();
      } catch (e) {
        console.warn(e);
      }
    },
    parseOrganizationResponse(responseData) {
      this.organizations = responseData['organizations']
      console.debug(`Organizations: ${this.organizations}`)

      this.currentOrganization = responseData['current_organization']
      console.debug(`Current Organization: ${this.currentOrganization}`)

      this.teams = responseData['teams']
      console.debug(`Teams: ${this.teams}`)

      this.currentTeam = responseData['current_team']
      console.debug(`Current Team: ${this.currentTeam}`)
    },
    setCurrentOrganization(groupUuid) {
      this.$http.post(`/api/groups/current_organization/${groupUuid}`)
          .then(response => {
            this.parseOrganizationResponse(response.data);
            this.loadLabels()
            // The bell shows the current organization's only
            this.problems = [];
            this.loadProblems();
          }).catch(function (e) {
        console.log(e)
      });
    },
    setCurrentTeam(groupUuid) {
      this.$http.post(`/api/groups/current_team/${groupUuid}`)
          .then(response => {
            this.parseOrganizationResponse(response.data);
          }).catch(function (e) {
        console.log(e)
      });
    },
    loadProblems() {
      // The server knows the current organization and who may see its problems: runners get none
      const request = (this.problemsRequest || 0) + 1;
      this.problemsRequest = request;
      this.$http.get('/api/notifications')
          .then(response => {
            // Only the latest: an older answer may be another organization's
            if (request === this.problemsRequest) {
              this.problems = response.data;
            }
          })
          .catch(e => {
            console.warn(e)
          });
    },
    dismissProblem(problem) {
      this.problems = this.problems.filter(item => item.uuid !== problem.uuid);
      this.$http.delete(`/api/notifications/${problem.uuid}`)
          .catch(e => {
            console.warn(e)
          });
    },
    markProblemsRead(uuids) {
      this.problems = this.problems.map(problem => uuids.includes(problem.uuid) ? {...problem, read: true} : problem);
      this.$http.post('/api/notifications/read', {uuids})
          .catch(e => {
            console.warn(e)
          });
    },
    dismissJob(job) {
      // Gone at once: the server sends the list without it too
      this.jobs = this.jobs.filter(item => item.uuid !== job.uuid);
      this.$http.delete(`/api/jobs/${job.uuid}`)
          .catch(e => {
            console.warn(e)
          });
    },
    onShortcutKeydown(event) {
      // Signed in only
      if (!this.activeUser || !this.activeUser.username) {
        return;
      }
      const shortcut = findShortcut(event, this.shortcuts);
      if (shortcut) {
        // Also keeps the browser's own use of the keys, if any, from running
        event.preventDefault();
        shortcut.handler(event);
      }
    },
    shortcutLabelOf(id) {
      return shortcutLabel(this.shortcuts.find(shortcut => shortcut.id === id));
    },
    openSearch() {
      // In the current organization
      if (!this.currentOrganization) {
        return;
      }
      this.$refs.globalSearch.open({
        placeholder: this.canViewRunners ? 'Search for Runners and Workouts' : 'Search for Workouts',
        groupTitles: true,
        // The server returns only what the user may open. Runners don't see users at all.
        // The pages to go to as well, after the results (OSA-174)
        providers: [
          ...globalSearchProviders({http: this.$http, router: this.$router, includeUsers: this.canViewRunners}),
          navigationProvider({router: this.$router, pages: this.navigationPages()}),
        ],
      });
    },
    /**
     * The pages to go to, as the navigation bar shows them to this user (OSA-136)
     */
    navigationPages() {
      return [
        {id: 'workouts', title: 'Workouts', icon: 'running', location: {name: 'workouts'}, show: true},
        {id: 'runners', title: 'Runners', icon: 'users', location: {name: 'runners'}, show: this.canViewRunners},
        {id: 'programs', title: 'Programs', icon: 'calendar-alt', location: {name: 'programs'}, show: this.canManagePrograms},
        {id: 'plans', title: 'Plans', icon: 'route', location: {name: 'plans'}, show: this.isSuperAdmin},
        {id: 'profile', title: 'Profile', icon: 'user', location: {name: 'profile'}, show: true},
        {id: 'jobs', title: 'Jobs', icon: 'cogs', location: {name: 'jobs'}, show: this.isSuperAdmin},
      ].filter(page => page.show);
    },
    openNavigation() {
      this.$refs.globalSearch.open({
        placeholder: 'Go to…',
        // All the pages at once: typing narrows them down
        minLength: 0,
        providers: [navigationProvider({router: this.$router, pages: this.navigationPages()})],
      });
    },
    openImpersonateSearch() {
      this.$refs.globalSearch.open({
        placeholder: 'Search for User to impersonate',
        providers: [
          usersProvider({
            http: this.$http,
            excludeUsernames: this.activeUser && this.activeUser.username ? [this.activeUser.username] : [],
            select: user => {
              impersonate(this.$http, user.uuid)
                  .then(() => {
                    this.loadActiveUser();
                    this.$router.push({name: 'profile'}).catch(() => {
                      // Already on the profile page
                    });
                  })
                  .catch(e => {
                    console.warn(e)
                  });
            },
          }),
        ],
      });
    },
    endImpersonation() {
      this.$http.post(`/api/sa/end_impersonation`)
          .then(() => {
            this.loadActiveUser();
          }).catch(function (e) {
        console.log(e)
      });
    },
    logout() {
      this.jobSocket.close();
      this.jobs = [];
      this.problems = [];
      this.$http.get(`/api/logout`).then(() => {
        getAuth().signOut().then(() => {
          this.currentOrganization = null;
          this.$router.push({name: 'login'})
        })
      })
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onShortcutKeydown);
    this.jobSocket.close();
  },
  created() {
    this.jobSocket = new JobSocket({
      // The server finds the session by the token: the API call makes sure it has one for a refreshed token
      getToken: () => this.$http.get('/api/auth').then(() => getAuth().currentUser.getIdToken()),
      onJobs: jobs => {
        this.jobs = jobs;
      },
      // Told when they change, and on (re)connect
      onNotifications: () => this.loadProblems(),
      // The connection dropped, e.g. the API restarted and lost this page's session
      onReconnect: () => this.restoreSession(),
    });
  },
  mounted() {
    window.addEventListener('keydown', this.onShortcutKeydown);

    let self = this;
    getAuth().onAuthStateChanged((user) => {
      this.user = user;
      if (this.user != null) {
        self.getOrganizations();
      }
    });

    if (this.user != null) {

      // We authenticate first to make sure that we have a fresh server-side session
      this.$http.get(`/api/auth`)
          .then(() => {
            // Also connects the jobs WebSocket, for the active user
            self.loadActiveUser();
            self.loadLabels();
          }).catch(function (e) {
        console.log(e)
      });
    }
  }
  ,
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  /*text-align: center;*/
  color: #2c3e50;

  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;

}

.router-view {
  margin-top: 30px;
  text-align: left;
}

#notifications-dropdown-header {
  margin-right: 10px;
}

.menu-item-with-shortcut {
  display: flex;
  align-items: center;
}

.menu-shortcut {
  margin-left: auto;
  padding-left: 1.5rem;
  color: #6c757d;
  font-size: 0.85em;
}

.dropdown-item:active .menu-shortcut {
  color: inherit;
}
</style>
